import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { Dropdown } from '../../components/Dropdown';
import { ErrorText } from '../../components/ErrorText';
import { FloatingField } from '../../components/FloatingField';
import { FormCard } from '../../components/FormCard';
import { Input } from '../../components/Input';
import { PhoneInput } from '../../components/PhoneInput';
import { AccountSideMenu } from '../../composites/AccountSideMenu';
import { MailIcon, MessageIcon } from '../../icons/NavIcons';
import {
  ContactMethodCard,
  ContactMethodIcon,
  ContactMethodLabel,
  ContactMethodRow,
  FormSection,
  HeaderRow,
  MainColumn,
  PageGrid,
  PageTitle,
  PageWrapper,
  PrivacyLine,
  PrivacyLink,
  SectionTitle,
  SplitRow,
  SubLabel,
  SubmitFooter,
  SubmitRow,
} from './AccountPersonalInformationTemplate.styles';
import type {
  AccountPersonalInformationAddress,
  AccountPersonalInformationLabels,
  AccountPersonalInformationTemplateProps,
} from './AccountPersonalInformationTemplate.types';

const DEFAULT_LABELS: Required<AccountPersonalInformationLabels> = {
  pageTitle: 'My Profile',
  activeStatus: 'Active',

  personalSectionTitle: 'Personal information',
  deliverySectionTitle: 'Delivery',
  paymentSectionTitle: 'Payment',
  marketingSectionTitle: 'Marketing',

  titleLabel: 'Title',
  firstNameLabel: 'First name',
  lastNameLabel: 'Last name',
  emailLabel: 'Email',
  phoneLabel: 'Phone',
  addressLine1Label: 'Address line 1',
  addressLine2Label: 'Address line 2',
  cityLabel: 'City',
  stateLabel: 'County',
  postalCodeLabel: 'Postcode',
  countryLabel: 'Country',
  countryPlaceholder: 'Select',
  phoneSearchPlaceholder: 'Search country',

  paymentSameAsDeliveryLabel: 'Same as delivery details',

  marketingOptInLabel: 'Send me offers and news',
  contactMethodLabel: 'How should we reach you?',
  contactMethodEmailLabel: 'Email',
  contactMethodSmsLabel: 'Text message',

  privacyPrefix: "We won't share your details. See our ",
  privacyLinkLabel: 'privacy policy',
  privacySuffix: '.',

  saveButtonLabel: 'Save changes',
};

export function AccountPersonalInformationTemplate({
  navItems,
  onSignOut,

  values,
  onChange,
  errors,

  titleOptions,
  countryOptions,
  phoneCountryCodes,

  isSubmitting = false,
  onSubmit,
  submitError,

  privacyHref,
  onPrivacyClick,

  sideMenuTitle,
  sideMenuSignOutLabel,
  sideMenuNavigationLabel,

  billingOnly = false,

  labels,
}: AccountPersonalInformationTemplateProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };

  const updateAddress =
    (section: 'delivery' | 'payment') =>
    <K extends keyof AccountPersonalInformationAddress>(
      key: K,
      value: AccountPersonalInformationAddress[K]
    ) => {
      onChange(section, { ...values[section], [key]: value });
    };

  const updateDelivery = updateAddress('delivery');
  const updatePayment = updateAddress('payment');

  const updatePersonal = <K extends keyof typeof values.personal>(
    key: K,
    value: (typeof values.personal)[K]
  ) => {
    onChange('personal', { ...values.personal, [key]: value });
  };

  const updateContactMethods = <K extends keyof typeof values.contactMethods>(
    key: K,
    value: (typeof values.contactMethods)[K]
  ) => {
    onChange('contactMethods', { ...values.contactMethods, [key]: value });
  };

  const renderAddressFields = (
    section: 'delivery' | 'payment',
    sectionValues: AccountPersonalInformationAddress,
    update: (
      key: keyof AccountPersonalInformationAddress,
      value: AccountPersonalInformationAddress[keyof AccountPersonalInformationAddress]
    ) => void
  ) => {
    const err = (field: keyof AccountPersonalInformationAddress) =>
      errors?.[`${section}.${field}` as keyof typeof errors];
    const sectionTitle =
      section === 'delivery' ? merged.deliverySectionTitle : merged.paymentSectionTitle;
    const a11yLabel = (fieldLabel: string) => `${sectionTitle} — ${fieldLabel}`;
    const fieldRequired = !billingOnly;
    const star = fieldRequired ? '*' : '';

    return (
      <>
        <FloatingField
          label={merged.titleLabel}
          hasValue={Boolean(sectionValues.title)}
          error={err('title')}
          required={fieldRequired}
        >
          <Dropdown
            options={titleOptions}
            value={sectionValues.title || undefined}
            placeholder={`${merged.titleLabel}${star}`}
            onChange={(v) => update('title', v)}
            aria-label={a11yLabel(merged.titleLabel)}
          />
        </FloatingField>

        <FloatingField
          label={merged.firstNameLabel}
          hasValue={Boolean(sectionValues.firstName)}
          error={err('firstName')}
          required={fieldRequired}
        >
          <Input
            type="text"
            autoComplete="given-name"
            value={sectionValues.firstName}
            onChange={(e) => update('firstName', e.target.value)}
            error={Boolean(err('firstName'))}
            placeholder={`${merged.firstNameLabel}${star}`}
            aria-label={a11yLabel(merged.firstNameLabel)}
          />
        </FloatingField>

        <FloatingField
          label={merged.lastNameLabel}
          hasValue={Boolean(sectionValues.lastName)}
          error={err('lastName')}
          required={fieldRequired}
        >
          <Input
            type="text"
            autoComplete="family-name"
            value={sectionValues.lastName}
            onChange={(e) => update('lastName', e.target.value)}
            error={Boolean(err('lastName'))}
            placeholder={`${merged.lastNameLabel}${star}`}
            aria-label={a11yLabel(merged.lastNameLabel)}
          />
        </FloatingField>

        <FloatingField
          label={merged.emailLabel}
          hasValue={Boolean(sectionValues.email)}
          error={err('email')}
          required={fieldRequired}
        >
          <Input
            type="email"
            autoComplete="email"
            value={sectionValues.email}
            onChange={(e) => update('email', e.target.value)}
            error={Boolean(err('email'))}
            placeholder={`${merged.emailLabel}${star}`}
            aria-label={a11yLabel(merged.emailLabel)}
          />
        </FloatingField>

        <FloatingField
          label={merged.phoneLabel}
          hasValue={Boolean(sectionValues.phone.number)}
          error={err('phone')}
          required={fieldRequired}
        >
          <PhoneInput
            value={sectionValues.phone}
            onChange={(phone) => update('phone', phone)}
            countryCodes={phoneCountryCodes}
            error={Boolean(err('phone'))}
            aria-label={a11yLabel(merged.phoneLabel)}
            placeholder={`${merged.phoneLabel}${star}`}
            searchPlaceholder={merged.phoneSearchPlaceholder}
            bare
          />
        </FloatingField>

        <FloatingField
          label={merged.addressLine1Label}
          hasValue={Boolean(sectionValues.line1)}
          error={err('line1')}
          required={fieldRequired}
        >
          <Input
            type="text"
            autoComplete="address-line1"
            value={sectionValues.line1}
            onChange={(e) => update('line1', e.target.value)}
            error={Boolean(err('line1'))}
            placeholder={`${merged.addressLine1Label}${star}`}
            aria-label={a11yLabel(merged.addressLine1Label)}
          />
        </FloatingField>

        <FloatingField
          label={merged.addressLine2Label}
          hasValue={Boolean(sectionValues.line2)}
          error={err('line2')}
          required={false}
        >
          <Input
            type="text"
            autoComplete="address-line2"
            value={sectionValues.line2}
            onChange={(e) => update('line2', e.target.value)}
            error={Boolean(err('line2'))}
            placeholder={merged.addressLine2Label}
            aria-label={a11yLabel(merged.addressLine2Label)}
          />
        </FloatingField>

        <FloatingField
          label={merged.cityLabel}
          hasValue={Boolean(sectionValues.city)}
          error={err('city')}
          required={fieldRequired}
        >
          <Input
            type="text"
            autoComplete="address-level2"
            value={sectionValues.city}
            onChange={(e) => update('city', e.target.value)}
            error={Boolean(err('city'))}
            placeholder={`${merged.cityLabel}${star}`}
            aria-label={a11yLabel(merged.cityLabel)}
          />
        </FloatingField>

        <SplitRow>
          <FloatingField
            label={merged.stateLabel}
            hasValue={Boolean(sectionValues.state)}
            error={err('state')}
            required={false}
          >
            <Input
              type="text"
              autoComplete="address-level1"
              value={sectionValues.state}
              onChange={(e) => update('state', e.target.value)}
              error={Boolean(err('state'))}
              placeholder={merged.stateLabel}
              aria-label={a11yLabel(merged.stateLabel)}
            />
          </FloatingField>

          <FloatingField
            label={merged.postalCodeLabel}
            hasValue={Boolean(sectionValues.postalCode)}
            error={err('postalCode')}
            required={fieldRequired}
          >
            <Input
              type="text"
              autoComplete="postal-code"
              value={sectionValues.postalCode}
              onChange={(e) => update('postalCode', e.target.value)}
              error={Boolean(err('postalCode'))}
              placeholder={`${merged.postalCodeLabel}${star}`}
              aria-label={a11yLabel(merged.postalCodeLabel)}
            />
          </FloatingField>
        </SplitRow>

        <FloatingField
          label={merged.countryLabel}
          hasValue={Boolean(sectionValues.country)}
          error={err('country')}
          required={fieldRequired}
        >
          <Dropdown
            options={countryOptions}
            value={sectionValues.country || undefined}
            placeholder={`${merged.countryLabel}${star}`}
            onChange={(v) => update('country', v)}
            aria-label={a11yLabel(merged.countryLabel)}
            withSearch
          />
        </FloatingField>
      </>
    );
  };

  return (
    <PageWrapper data-testid="account-personal-information-template">
      <PageGrid>
        <AccountSideMenu
          items={navItems}
          onSignOut={onSignOut}
          title={sideMenuTitle}
          signOutLabel={sideMenuSignOutLabel}
          navigationLabel={sideMenuNavigationLabel}
        />

        <MainColumn>
          <HeaderRow>
            <PageTitle>{merged.pageTitle}</PageTitle>
            <Badge tone="success">{merged.activeStatus}</Badge>
          </HeaderRow>

          <FormSection>
            <SectionTitle>{merged.personalSectionTitle}</SectionTitle>
            <FormCard>
              <FloatingField
                label={merged.titleLabel}
                hasValue={Boolean(values.personal.title)}
                error={errors?.['personal.title']}
              >
                <Dropdown
                  options={titleOptions}
                  value={values.personal.title || undefined}
                  placeholder={`${merged.titleLabel}*`}
                  onChange={(v) => updatePersonal('title', v)}
                  aria-label={merged.titleLabel}
                />
              </FloatingField>
              <FloatingField
                label={merged.firstNameLabel}
                hasValue={Boolean(values.personal.firstName)}
                error={errors?.['personal.firstName']}
              >
                <Input
                  type="text"
                  autoComplete="given-name"
                  value={values.personal.firstName}
                  onChange={(e) => updatePersonal('firstName', e.target.value)}
                  error={Boolean(errors?.['personal.firstName'])}
                  placeholder={`${merged.firstNameLabel}*`}
                  aria-label={merged.firstNameLabel}
                />
              </FloatingField>
              <FloatingField
                label={merged.lastNameLabel}
                hasValue={Boolean(values.personal.lastName)}
                error={errors?.['personal.lastName']}
              >
                <Input
                  type="text"
                  autoComplete="family-name"
                  value={values.personal.lastName}
                  onChange={(e) => updatePersonal('lastName', e.target.value)}
                  error={Boolean(errors?.['personal.lastName'])}
                  placeholder={`${merged.lastNameLabel}*`}
                  aria-label={merged.lastNameLabel}
                />
              </FloatingField>
            </FormCard>
          </FormSection>

          {billingOnly ? (
            <FormSection>
              <SectionTitle>{merged.paymentSectionTitle}</SectionTitle>
              <FormCard>{renderAddressFields('payment', values.payment, updatePayment)}</FormCard>
            </FormSection>
          ) : (
            <>
              <FormSection>
                <SectionTitle>{merged.deliverySectionTitle}</SectionTitle>
                <FormCard>
                  {renderAddressFields('delivery', values.delivery, updateDelivery)}
                </FormCard>
              </FormSection>

              <FormSection>
                <SectionTitle>{merged.paymentSectionTitle}</SectionTitle>
                <Checkbox
                  checked={values.paymentSameAsDelivery}
                  onChange={(checked) => onChange('paymentSameAsDelivery', checked)}
                  label={merged.paymentSameAsDeliveryLabel}
                />
                {!values.paymentSameAsDelivery && (
                  <FormCard>
                    {renderAddressFields('payment', values.payment, updatePayment)}
                  </FormCard>
                )}
              </FormSection>
            </>
          )}

          <FormSection>
            <SectionTitle>{merged.marketingSectionTitle}</SectionTitle>
            <Checkbox
              checked={values.marketingOptIn}
              onChange={(checked) => onChange('marketingOptIn', checked)}
              label={merged.marketingOptInLabel}
            />

            <SubLabel>{merged.contactMethodLabel}</SubLabel>
            <ContactMethodRow role="group" aria-label={merged.contactMethodLabel}>
              <ContactMethodCard $selected={values.contactMethods.email}>
                <Checkbox
                  checked={values.contactMethods.email}
                  onChange={(checked) => updateContactMethods('email', checked)}
                  disabled={!values.marketingOptIn}
                  label={
                    <>
                      <ContactMethodIcon>
                        <MailIcon size={18} />
                      </ContactMethodIcon>
                      <ContactMethodLabel>{merged.contactMethodEmailLabel}</ContactMethodLabel>
                    </>
                  }
                />
              </ContactMethodCard>

              <ContactMethodCard $selected={values.contactMethods.sms}>
                <Checkbox
                  checked={values.contactMethods.sms}
                  onChange={(checked) => updateContactMethods('sms', checked)}
                  disabled={!values.marketingOptIn}
                  label={
                    <>
                      <ContactMethodIcon>
                        <MessageIcon size={18} />
                      </ContactMethodIcon>
                      <ContactMethodLabel>{merged.contactMethodSmsLabel}</ContactMethodLabel>
                    </>
                  }
                />
              </ContactMethodCard>
            </ContactMethodRow>

            <PrivacyLine>
              {merged.privacyPrefix}
              <PrivacyLink
                href={privacyHref}
                onClick={onPrivacyClick}
                target={privacyHref ? '_blank' : undefined}
                rel={privacyHref ? 'noreferrer' : undefined}
              >
                {merged.privacyLinkLabel}
              </PrivacyLink>
              {merged.privacySuffix}
            </PrivacyLine>
          </FormSection>

          <SubmitFooter>
            <SubmitRow>
              <Button
                type="button"
                variant="primary"
                size="medium"
                onClick={onSubmit}
                loading={isSubmitting}
              >
                {merged.saveButtonLabel}
              </Button>
            </SubmitRow>
            {submitError && <ErrorText role="alert">{submitError}</ErrorText>}
          </SubmitFooter>
        </MainColumn>
      </PageGrid>
    </PageWrapper>
  );
}
